import chameleon from '@chamaeleonidae/chmln';
import { CHAMELEON } from '../constants';

let isInitialized = false;
let isUserIdentified = false;
let queuedExperienceId: string | null = null;

// Access the global chmln object for methods like show()
// Note: Window.chmln type is declared in search/PleriAnnouncements.tsx
// We cast to any at usage sites to avoid duplicate declaration conflicts.

export interface ChameleonUserInfo {
  userId: string;
  email: string;
  organizationId?: string;
  tenantId?: string;
  name?: string;
  /** Asset ownership is on for them: the ownership tour is shown only then. */
  assetOwnership?: boolean;
}

export const initializeChameleon = (): void => {
  if (isInitialized) {
    return;
  }

  try {
    chameleon.init(CHAMELEON.clientKey, {
      fastUrl: 'https://fast.chameleon.io/',
    });
    isInitialized = true;
  } catch (error) {
    console.error('Failed to initialize Chameleon:', error);
  }
};

const showQueuedExperience = (): void => {
  if (queuedExperienceId && isUserIdentified) {
    try {
      const chmln = (window as any).chmln;
      if (chmln && chmln.show) {
        chmln.show(queuedExperienceId);
        queuedExperienceId = null;
      }
    } catch (error) {
      console.error('Failed to show queued Chameleon experience:', error);
    }
  }
};

type ChameleonNavigateHandler = (to: string) => void;

let navigationHandler: ChameleonNavigateHandler | null = null;
let isNavigationHookRegistered = false;

/**
 * Chameleon's internal `Url.changeTo` performs a hard `window.location`
 * navigation. Patch it so same-origin URLs route through the registered SPA
 * navigation handler instead; true external URLs keep the original behavior.
 */
const interceptorChameleonRouter = (): void => {
  const maxAttempts = 10; // Try for up to 5 seconds
  let attempts = 0;
  const interceptor = setInterval(() => {
    attempts++;
    const chmln = (window as any).chmln;
    if (!(chmln && chmln.lib && chmln.lib.Url)) {
      if (attempts >= maxAttempts) {
        clearInterval(interceptor);
      }
      return;
    }
    clearInterval(interceptor);
    const originalChangeTo = chmln.lib.Url.changeTo;

    chmln.lib.Url.changeTo = function (this: unknown, href: string) {
      if (href.startsWith('/') || href.includes(window.location.hostname)) {
        const urlObj = href.startsWith('/')
          ? new URL(href, window.location.origin)
          : new URL(href);
        const relativePath = urlObj.pathname + urlObj.search + urlObj.hash;

        console.info(
          'Intercepted Chameleon hard reload — routing through SPA instead:',
          relativePath,
        );

        chmln.lib.On.trigger('app:navigate', [{ to: relativePath }]);
        return;
      }

      return originalChangeTo.apply(this, [href]);
    };
  }, 500);
};

/**
 * Routes Chameleon's built-in "navigate to URL" button action through the
 * app's SPA router instead of a full page load. Chameleon emits
 * `app:navigate` whenever a handler is registered; without one it falls back
 * to `window.location`. Registered once — callers can safely re-invoke to
 * swap in a fresh `navigate` function (e.g. on remount).
 */
export const setChameleonNavigationHandler = (
  handler: ChameleonNavigateHandler,
): void => {
  navigationHandler = handler;

  if (isNavigationHookRegistered) {
    return;
  }
  if (!isInitialized) {
    initializeChameleon();
  }

  try {
    const chmln = (window as any).chmln;
    if (chmln && chmln.on) {
      chmln.on('app:navigate', (opts: { to?: string }) => {
        if (opts?.to) {
          navigationHandler?.(opts.to);
        }
      });
      isNavigationHookRegistered = true;
      interceptorChameleonRouter();
    }
  } catch (error) {
    console.error('Failed to register Chameleon navigation handler:', error);
  }
};

export const updateChameleonUser = (userInfo: ChameleonUserInfo): void => {
  if (!isInitialized) {
    initializeChameleon();
  }

  try {
    chameleon.identify(userInfo.userId, {
      email: userInfo.email,
      name: userInfo.name,
      organizationId: userInfo.organizationId,
      tenantId: userInfo.tenantId,
      firstName: userInfo.name?.split(' ')?.[0],
      assetOwnership: userInfo.assetOwnership,
    });
    isUserIdentified = true;
    showQueuedExperience();
  } catch (error) {
    console.error('Failed to update Chameleon user:', error);
  }
};
